import { useEffect, useRef } from 'react';

const GOOGLE_IDENTITY_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const ONE_TAP_DISMISS_UNTIL_KEY = 'omniscribe:google_one_tap_dismiss_until';
const DEFAULT_COOLDOWN_MS = 24 * 60 * 60 * 1000;

let googleIdentityScriptPromise: Promise<void> | null = null;

function loadGoogleIdentityScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }

  if (googleIdentityScriptPromise) {
    return googleIdentityScriptPromise;
  }

  googleIdentityScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${GOOGLE_IDENTITY_SCRIPT_SRC}"]`
    );
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('failed to load google identity script')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.src = GOOGLE_IDENTITY_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('failed to load google identity script'));
    document.head.appendChild(script);
  });

  return googleIdentityScriptPromise;
}

function isInDismissCooldown(storageKey: string): boolean {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return false;
    }
    const dismissUntil = Number(raw);
    if (!Number.isFinite(dismissUntil)) {
      return false;
    }
    return Date.now() < dismissUntil;
  } catch {
    return false;
  }
}

function markDismissCooldown(storageKey: string, cooldownMs: number): void {
  if (cooldownMs <= 0) {
    return;
  }
  try {
    localStorage.setItem(storageKey, String(Date.now() + cooldownMs));
  } catch {
    // Ignore storage write failures.
  }
}

function shouldMarkDismissCooldown(reason: string | undefined): boolean {
  if (!reason) {
    return false;
  }
  return reason === 'tap_outside' || reason === 'user_cancel' || reason === 'cancel_called';
}

export interface UseGoogleOneTapOptions {
  enabled: boolean;
  clientId?: string;
  onCredential: (credential: string) => Promise<void> | void;
  onError?: (error: string) => void;
  cooldownMs?: number;
  storageKey?: string;
}

export function useGoogleOneTap({
  enabled,
  clientId,
  onCredential,
  onError,
  cooldownMs = DEFAULT_COOLDOWN_MS,
  storageKey = ONE_TAP_DISMISS_UNTIL_KEY,
}: UseGoogleOneTapOptions): void {
  const hasPromptedRef = useRef(false);
  const isHandlingCredentialRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      hasPromptedRef.current = false;
      isHandlingCredentialRef.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const normalizedClientID = clientId?.trim();
    if (!normalizedClientID) {
      return;
    }

    if (hasPromptedRef.current) {
      return;
    }

    if (isInDismissCooldown(storageKey)) {
      return;
    }

    let cancelled = false;

    const setupOneTap = async () => {
      try {
        await loadGoogleIdentityScript();
      } catch {
        onError?.('google_one_tap_unavailable');
        return;
      }

      if (cancelled) {
        return;
      }

      const googleOneTap = window.google?.accounts?.id;
      if (!googleOneTap) {
        onError?.('google_one_tap_unavailable');
        return;
      }

      if (hasPromptedRef.current) {
        return;
      }
      hasPromptedRef.current = true;

      googleOneTap.initialize({
        client_id: normalizedClientID,
        auto_select: false,
        cancel_on_tap_outside: true,
        callback: async (response) => {
          const credential = response.credential?.trim();
          if (!credential) {
            return;
          }
          if (isHandlingCredentialRef.current) {
            return;
          }
          isHandlingCredentialRef.current = true;
          try {
            await onCredential(credential);
          } finally {
            isHandlingCredentialRef.current = false;
          }
        },
      });

      googleOneTap.prompt((notification) => {
        if (!notification?.isDismissedMoment?.()) {
          return;
        }
        const reason = notification.getDismissedReason?.();
        if (shouldMarkDismissCooldown(reason)) {
          markDismissCooldown(storageKey, cooldownMs);
        }
      });
    };

    void setupOneTap();

    return () => {
      cancelled = true;
    };
  }, [enabled, clientId, cooldownMs, onCredential, onError, storageKey]);
}
